const LAST_VISITED_ROUTE_STORAGE_KEY = "wishlist:last-visited-route";

/**
 * Check whether a route should be persisted as the last authenticated page.
 *
 * The landing page, startup page, authentication routes, onboarding
 * routes, and explicit public routes are not stored.
 *
 * @param {import("vue-router").RouteLocationNormalizedLoaded | import("vue-router").RouteLocationNormalized} route Route snapshot
 * @returns {boolean} true when the route should be stored
 */
export function shouldPersistLastVisitedRoute(route) {
  if (!route || typeof route.path !== "string") {
    return false;
  }

  if (route.path === "/") {
    return false;
  }

  if (route.meta?.authPage === true) {
    return false;
  }

  if (route.meta?.onboardingStep === true) {
    return false;
  }

  if (route.meta?.startupPage === true) {
    return false;
  }

  if (route.meta?.publicPage === true) {
    return false;
  }

  return true;
}

/**
 * Persist the current route full path in local storage.
 *
 * @param {import("vue-router").RouteLocationNormalizedLoaded | import("vue-router").RouteLocationNormalized} route Route snapshot
 */
export function persistLastVisitedRoute(route) {
  if (!shouldPersistLastVisitedRoute(route)) {
    return;
  }

  const fullPath = typeof route.fullPath === "string" ? route.fullPath : route.path;
  if (!fullPath) {
    return;
  }

  try {
    window.localStorage.setItem(LAST_VISITED_ROUTE_STORAGE_KEY, fullPath);
  } catch (error) {
    // Ignore storage errors so navigation remains functional.
  }
}

/**
 * Read the stored last visited route from local storage.
 *
 * @returns {string} stored route full path or an empty string
 */
export function readStoredLastVisitedRoute() {
  try {
    return window.localStorage.getItem(LAST_VISITED_ROUTE_STORAGE_KEY) || "";
  } catch (error) {
    return "";
  }
}

/**
 * Clear any previously stored route resume target.
 */
export function clearStoredLastVisitedRoute() {
  try {
    window.localStorage.removeItem(LAST_VISITED_ROUTE_STORAGE_KEY);
  } catch (error) {
    // Ignore storage errors during cleanup.
  }
}

/**
 * Resolve the stored route against the router table.
 *
 * Invalid targets are cleared to avoid future redirect loops.
 *
 * @param {import("vue-router").Router} router Vue Router instance
 * @returns {string} validated route full path or an empty string
 */
export function resolveStoredLastVisitedRoute(router) {
  const storedRoute = readStoredLastVisitedRoute();
  if (!storedRoute) {
    return "";
  }

  const resolvedRoute = router.resolve(storedRoute);
  if (
    !resolvedRoute ||
    !Array.isArray(resolvedRoute.matched) ||
    resolvedRoute.matched.length === 0 ||
    resolvedRoute.meta?.startupPage === true
  ) {
    clearStoredLastVisitedRoute();
    return "";
  }

  return resolvedRoute.fullPath || storedRoute;
}
